/**
 * `TeamMemberCompetenceDetail` — dettaglio di una competenza per un membro del
 * Team, in **sola lettura**.
 *
 * Drill-down dalla dashboard del membro (`TeamMemberDashboard`): mostra le skill
 * della competenza con i voti della persona (Self / Reviewer / Manager) e la
 * media pesata, senza alcuna possibilità di modifica (a differenza di
 * `CompetenceDetail`, che permette di modificare il proprio Self al volo).
 *
 * Dati:
 *  - `GET /api/competences` → risolve la competenza dall'`:competenceId` e il
 *    suo elenco di `skillIds`;
 *  - `GET /api/skills` → nomi delle skill (anche quelle non ancora valutate);
 *  - `GET /api/dashboard/users/:userId/assessments` → i voti della persona;
 *  - `GET /api/dashboard/users/:userId` → match/rank della competenza (per il
 *    donut in intestazione) e il nome della persona.
 *
 * L'accesso è aperto a ogni ruolo autenticato (come il resto della sezione
 * Livelli).
 */
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  AssessmentView,
  CompetenceDto,
  SkillDto,
  TeamMemberDashboardDto,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { ABSENT_INDICATOR } from '../components/ScoreTriple';
import { ScoreBadge } from '../components/ScoreBadge';
import { CertificationCountBadge } from '../components/CertificationCountBadge';
import { ScoreCalcGuide } from '../components/ScoreCalcGuide';
import { ScoreGuide } from '../components/ScoreGuide';
import { propensionLabel } from '../components/PropensionSelect';
import { LivelloBadge } from '../components/LivelloBadge';
import { LivelloGuide } from '../components/LivelloGuide';
import { reviewsLevelRank } from '../components/SkillAssessmentTable';
import { scoreColor } from '../components/StarRating';
import {
  Alert,
  Button,
  Card,
  DataTable,
  DotRating,
  EmptyState,
  InfoHint,
  Loading,
  PageHeader,
  type Column,
  type SortDirection,
} from '../components/ui';
import type { ExperienceRange, Livello } from '../shared/dto';
import {
  DEFAULT_SKILL_WEIGHT,
  EXPERIENCE_RANGE_LABELS,
  EXPERIENCE_RANGE_VALUES,
  LIVELLO_VALUES,
  MAX_SKILL_WEIGHT,
  livelloRank,
} from '../shared/dto';

/** Etichetta descrittiva del peso, allineata a `SkillWeightEditor`. */
const WEIGHT_HINT: Record<number, string> = {
  1: 'Marginale',
  2: 'Bassa',
  3: 'Media',
  4: 'Alta',
  5: 'Essenziale',
};

/** Formatta una percentuale `0..100` come intero con `%`. */
function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/** Raggio del cerchio del donut (viewBox 36×36, stroke 4 → centro a 18). */
const DONUT_RADIUS = 16;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/**
 * `ScoreDonut` — anello di progresso di una percentuale `0..100`, identico a
 * quello della `CompetencyMatchCard` e della dashboard personale. Qui
 * rappresenta la **Copertura** della competenza; `label` personalizza il testo
 * accessibile.
 */
function ScoreDonut({ score, label }: { score: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, score));
  const rounded = Math.round(clamped);
  const ringColor = scoreColor(clamped / 10) ?? 'var(--color-primary)';
  const dash = (clamped / 100) * DONUT_CIRCUMFERENCE;

  return (
    <div
      className="competency-match-card__donut"
      role="img"
      aria-label={`${label} ${rounded} su 100`}
      title={`${label} ${rounded}/100`}
    >
      <svg viewBox="0 0 36 36" className="competency-match-card__donut-svg" aria-hidden="true">
        <circle className="competency-match-card__donut-track" cx="18" cy="18" r={DONUT_RADIUS} />
        <circle
          className="competency-match-card__donut-fill"
          cx="18"
          cy="18"
          r={DONUT_RADIUS}
          stroke={ringColor}
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className="competency-match-card__donut-value" aria-hidden="true">
        {rounded}
      </span>
    </div>
  );
}

/** Numero totale di recensioni (reviewer + manager) considerate nel rango. */
function reviewsCount(row: AssessmentView): number {
  return row.reviewerCount + row.managerCount;
}

/**
 * Converte un rango continuo `[0,5]` nel Livello più vicino (arrotondamento al
 * valore ordinale più prossimo), così da poterlo mostrare come `LivelloBadge`.
 * Il rango è 0-based (`0 → e-0` "Nessuno" … `5 → e-5`), coerente con
 * `livelloRank` dei DTO condivisi.
 */
function rankToLivello(rank: number): Livello {
  const index = Math.min(
    LIVELLO_VALUES.length - 1,
    Math.max(0, Math.round(rank)),
  );
  return LIVELLO_VALUES[index];
}

/** Confronto lessicografico deterministico dei nomi skill (già orientato). */
function compareSkillName(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const sign = direction === 'desc' ? -1 : 1;
  if (a.skillName < b.skillName) return -sign;
  if (a.skillName > b.skillName) return sign;
  return 0;
}

/**
 * Comparatore per la colonna "Livello (Self)": ordina per rango della scala
 * (`e-1..e-5`), con i livelli assenti (`null`) sempre in coda.
 */
function compareLivelloSelf(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const la = a.livelloSelf;
  const lb = b.livelloSelf;
  if (la === null && lb === null) return 0;
  if (la === null) return 1;
  if (lb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (livelloRank(la) - livelloRank(lb));
}

/** Comparatore per la colonna "Esperienza": rango della scala di fasce, `null` in coda. */
function compareExperience(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const ra = a.yearsOfExperience;
  const rb = b.yearsOfExperience;
  if (ra === null && rb === null) return 0;
  if (ra === null) return 1;
  if (rb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return (
    sign *
    (EXPERIENCE_RANGE_VALUES.indexOf(ra) - EXPERIENCE_RANGE_VALUES.indexOf(rb))
  );
}

/**
 * Comparatore per la colonna "Propensione" (`selfInterest`): il
 * valore `0` è un voto valido, i `null` sempre in coda.
 */
function compareInterest(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = a.selfInterest;
  const vb = b.selfInterest;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Comparatore per la colonna "Reviews": rango di Livello aggregato, `null` in coda. */
function compareReviews(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = reviewsLevelRank(a);
  const vb = reviewsLevelRank(b);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Comparatore per la colonna "Score": Score_Skill derivato dall'API, `null` in coda. */
function compareScore(
  a: AssessmentView,
  b: AssessmentView,
  direction: SortDirection,
): number {
  const va = a.score;
  const vb = b.score;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Rende la fascia di anni di esperienza dichiarata, o "—" se assente. */
function experienceCell(value: ExperienceRange | null) {
  if (value === null) {
    return (
      <span className="skill-list__absent" aria-label="Esperienza: non dichiarata">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  return <span className="skill-list__years">{EXPERIENCE_RANGE_LABELS[value]}</span>;
}

/**
 * Rende la Propensione come barra di avanzamento (`progress`) sulla scala
 * `0..10`: la larghezza del riempimento è proporzionale al voto e l'etichetta
 * testuale è esposta come tooltip e testo accessibile. Se il valore è assente
 * (`null`) mostra l'indicatore di assenza "—". Dato puramente informativo,
 * reso in modo identico alla lista skill e alla dashboard del membro.
 */
function interestCell(value: number | null, label = 'Propensione') {
  if (value === null) {
    return (
      <span
        className="skill-list__absent"
        aria-label={`${label}: nessuna preferenza`}
      >
        {ABSENT_INDICATOR}
      </span>
    );
  }
  const percent = (value / 10) * 100;
  const text = propensionLabel(value);
  return (
    <span
      className="skill-list__propension-bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={10}
      aria-valuenow={value}
      aria-valuetext={text}
      aria-label={label}
      title={`${text} (${value}/10)`}
    >
      <span
        className="skill-list__propension-bar-fill"
        style={{ width: `${percent}%` }}
      />
    </span>
  );
}

/**
 * Rende la colonna "Reviews": il Livello aggregato reviewer/manager come badge
 * di Livello (rango continuo arrotondato) con il numero di recensioni; "—"
 * quando nessuno ha votato.
 */
function reviewsCell(row: AssessmentView) {
  const rank = reviewsLevelRank(row);
  if (rank === null) {
    return (
      <span className="skill-list__absent" aria-label="Reviews: nessun voto">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  const count = reviewsCount(row);
  return (
    <span className="skill-list__reviews">
      <LivelloBadge livello={rankToLivello(rank)} />
      <span
        className="skill-list__reviews-count"
        title={`${count} ${count === 1 ? 'recensione' : 'recensioni'}`}
        aria-label={`${count} ${count === 1 ? 'recensione' : 'recensioni'}`}
      >
        {count}
      </span>
    </span>
  );
}

/**
 * Rende lo "Score" (Score_Skill derivato) come badge colorato, o "—" se assente,
 * affiancato dal badge del numero di certificazioni valide che vi contribuiscono.
 */
function scoreBadgeCell(row: AssessmentView) {
  const certBadge = (
    <CertificationCountBadge count={row.certificationCount} skillName={row.skillName} />
  );
  return (
    <span className="skill-list__score-cell">
      {row.score === null ? (
        <span className="skill-list__absent" aria-label="Score: assente">
          {ABSENT_INDICATOR}
        </span>
      ) : (
        <ScoreBadge value={row.score} label="Score" />
      )}
      {certBadge}
    </span>
  );
}

/** Comparatore per la colonna "Peso" (peso della skill sulla competenza). */
function makeWeightComparator(weightOf: (skillId: string) => number) {
  return (
    a: AssessmentView,
    b: AssessmentView,
    direction: SortDirection,
  ): number => {
    const sign = direction === 'desc' ? -1 : 1;
    return sign * (weightOf(a.skillId) - weightOf(b.skillId));
  };
}

/**
 * Colonne in sola lettura sul modello Livello: Skill + Peso + Livello +
 * Esperienza + Propensione + Reviews + Score_Skill derivato.
 * Nessuna colonna "Azioni" né picker di modifica (a differenza di
 * `CompetenceDetail`).
 */
function buildColumns(
  weightOf: (skillId: string) => number,
  descriptionBySkillId?: Map<string, string | undefined>,
): Column<AssessmentView>[] {
  return [
    {
      key: 'skillName',
      header: 'Skill',
      sortable: true,
      sortComparator: compareSkillName,
      render: (row) => (
        <span className="skill-list__skill-cell">
          <span className="skill-list__skill-name">{row.skillName}</span>
          <InfoHint
            text={descriptionBySkillId?.get(row.skillId)}
            label={`Descrizione di ${row.skillName}`}
          />
        </span>
      ),
    },
    {
      key: 'weight',
      header: 'Peso',
      sortable: true,
      sortComparator: makeWeightComparator(weightOf),
      render: (row) => {
        const weight = weightOf(row.skillId);
        return (
          <DotRating
            value={weight}
            onChange={() => {}}
            readOnly
            size="sm"
            max={MAX_SKILL_WEIGHT}
            label={`Peso di ${row.skillName}`}
            optionLabel={(n) => `${n} — ${WEIGHT_HINT[n] ?? ''}`.trim()}
          />
        );
      },
      className: 'skill-list__score-col',
    },
    {
      key: 'livelloSelf',
      header: (
        <span className="score-calc-guide__header">
          Livello
          <LivelloGuide className="score-calc-guide__trigger" />
        </span>
      ),
      sortable: true,
      sortComparator: compareLivelloSelf,
      render: (row) => (
        <span className="skill-list__level-cell">
          <LivelloBadge livello={row.livelloSelf} />
        </span>
      ),
      className: 'skill-list__level-col',
    },
    {
      key: 'experience',
      header: 'Esperienza',
      sortable: true,
      sortComparator: compareExperience,
      render: (row) => experienceCell(row.yearsOfExperience),
      className: 'skill-list__experience-col',
    },
    {
      key: 'interest',
      header: 'Propensione',
      sortable: true,
      sortComparator: compareInterest,
      render: (row) => interestCell(row.selfInterest),
      className: 'skill-list__score-col skill-list__score-col--self',
    },
    {
      key: 'reviews',
      header: 'Reviews',
      sortable: true,
      sortComparator: compareReviews,
      render: (row) => reviewsCell(row),
      className: 'skill-list__score-col skill-list__reviews-col',
    },
    {
      key: 'score',
      header: (
        <span className="score-calc-guide__header">
          Score
          <ScoreCalcGuide />
        </span>
      ),
      sortable: true,
      sortComparator: compareScore,
      render: (row) => scoreBadgeCell(row),
      className: 'skill-list__score-col',
    },
  ];
}

export default function TeamMemberCompetenceDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { userId, competenceId } = useParams<{
    userId: string;
    competenceId: string;
  }>();

  const competencesQuery = useQuery<CompetenceDto[]>({
    queryKey: ['competences'],
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });

  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: ['skills'],
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  const assessmentsQuery = useQuery<AssessmentView[]>({
    queryKey: ['dashboard', 'users', userId, 'assessments'],
    queryFn: () =>
      api.get<AssessmentView[]>(
        `/api/dashboard/users/${encodeURIComponent(userId ?? '')}/assessments`,
      ),
    enabled: userId !== undefined,
  });

  // Match/rank della competenza + identità della persona (stesso payload della
  // dashboard del membro). Condiviso con `TeamMemberDashboard` via query key.
  const memberQuery = useQuery<TeamMemberDashboardDto>({
    queryKey: ['dashboard', 'users', userId],
    queryFn: () =>
      api.get<TeamMemberDashboardDto>(
        `/api/dashboard/users/${encodeURIComponent(userId ?? '')}`,
      ),
    enabled: userId !== undefined,
  });

  const competence = useMemo(
    () => (competencesQuery.data ?? []).find((c) => c.id === competenceId) ?? null,
    [competencesQuery.data, competenceId],
  );

  const match = useMemo(
    () =>
      (memberQuery.data?.dashboard.competencyMatches ?? []).find(
        (m) => m.competenceId === competenceId,
      ) ?? null,
    [memberQuery.data, competenceId],
  );

  // Righe della tabella: le skill della competenza (nell'ordine di `skillIds`),
  // arricchite con i voti della persona quando presenti.
  const rows = useMemo<AssessmentView[]>(() => {
    if (!competence) return [];
    const skillById = new Map((skillsQuery.data ?? []).map((s) => [s.id, s]));
    const assessmentBySkill = new Map(
      (assessmentsQuery.data ?? []).map((a) => [a.skillId, a]),
    );
    return competence.skillIds
      .map((skillId) => {
        const existing = assessmentBySkill.get(skillId);
        if (existing) return existing;
        const skill = skillById.get(skillId);
        if (!skill) return null;
        return {
          skillId,
          skillName: skill.name,
          selfInterest: null,
          aiLevel: null,
          reviewerLevelRank: null,
          reviewerCount: 0,
          reviewerUniformLevel: null,
          managerLevelRank: null,
          managerCount: 0,
          managerUniformLevel: null,
          // Vista team in sola lettura: il Livello dichiarato è propagato dal
          // dato server; per le skill non ancora valutate è assente (`null`).
          livelloSelf: null,
          yearsOfExperience: null,
          score: null,
          // Skill non valutata: nessuna certificazione contribuisce allo Score.
          certificationCount: 0,
          // Skill non valutata: nessuna revisione.
          reviews: [],
        } satisfies AssessmentView;
      })
      .filter((r): r is AssessmentView => r !== null);
  }, [competence, skillsQuery.data, assessmentsQuery.data]);

  // Peso (1..5) di una skill sulla competenza corrente; default 1 se assente.
  const skillWeights = competence?.skillWeights;
  const weightOf = useMemo(() => {
    const map = skillWeights ?? {};
    return (skillId: string) => map[skillId] ?? DEFAULT_SKILL_WEIGHT;
  }, [skillWeights]);

  const descriptionBySkillId = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const s of skillsQuery.data ?? []) map.set(s.id, s.description);
    return map;
  }, [skillsQuery.data]);

  const columns = useMemo(
    () => buildColumns(weightOf, descriptionBySkillId),
    [weightOf, descriptionBySkillId],
  );

  const backToMember = () => {
    void navigate(`/insights/team/${encodeURIComponent(userId ?? '')}`);
  };

  // ----- Rendering -----

  if (competencesQuery.isLoading) {
    return (
      <section aria-labelledby="team-competence-title">
        <h1 id="team-competence-title">Competenza</h1>
        <Loading>Caricamento della competenza…</Loading>
      </section>
    );
  }

  if (competencesQuery.isError) {
    const message =
      competencesQuery.error instanceof Error
        ? competencesQuery.error.message
        : 'Impossibile caricare la competenza.';
    return (
      <section aria-labelledby="team-competence-title">
        <h1 id="team-competence-title">Competenza</h1>
        <Alert tone="error">{message}</Alert>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToMember}>
            ← Torna alla dashboard
          </Button>
        </div>
      </section>
    );
  }

  if (competence === null) {
    return (
      <section aria-labelledby="team-competence-title">
        <h1 id="team-competence-title">Competenza</h1>
        <EmptyState role="status">
          La competenza richiesta non esiste o non è più disponibile.
        </EmptyState>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToMember}>
            ← Torna alla dashboard
          </Button>
        </div>
      </section>
    );
  }

  const memberName = memberQuery.data?.displayName;
  const subtitle = memberName
    ? `Skill della competenza per ${memberName}: Self, media Reviewer e Manager. Sola lettura.`
    : 'Skill della competenza con Self, media Reviewer e Manager. Sola lettura.';

  return (
    <section className="competence-detail" aria-labelledby="team-competence-title">
      <PageHeader
        title="Dettaglio competenza"
        titleId="team-competence-title"
        subtitle={subtitle}
        actions={
          <Button type="button" variant="ghost" onClick={backToMember}>
            ← Torna alla dashboard
          </Button>
        }
      />

      {/* Intestazione della competenza selezionata; a destra il donut della
          Copertura (stesso valore della card della dashboard). Accanto al nome
          la sola Proficiency, coerente con la dashboard personale. */}
      <div className="competence-picker__selected">
        <div>
          <span className="competence-picker__selected-label">
            {match ? (
              <>
                Proficiency{' '}
                {match.proficiencyPercent === null
                  ? '—'
                  : formatPercent(match.proficiencyPercent)}
              </>
            ) : (
              'Competenza'
            )}
          </span>
          <h2 className="competence-picker__selected-name">{competence.name}</h2>
        </div>
        {match && (
          <div className="competence-detail__score">
            <ScoreGuide className="competence-detail__score-guide" />
            <ScoreDonut score={match.coveragePercent} label="Copertura" />
          </div>
        )}
      </div>

      {/* Descrizione e mission affiancate, sotto la card del nome. Ogni card
          compare solo se il rispettivo campo è valorizzato. */}
      {(competence.description || competence.mission) && (
        <div className="competence-detail__info">
          {competence.description && (
            <Card className="competence-detail__info-card">
              <h3 className="competence-detail__info-title">Descrizione</h3>
              <p className="competence-detail__info-text">{competence.description}</p>
            </Card>
          )}
          {competence.mission && (
            <Card className="competence-detail__info-card">
              <h3 className="competence-detail__info-title">Mission</h3>
              <p className="competence-detail__info-text">{competence.mission}</p>
            </Card>
          )}
        </div>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.skillId}
        loading={skillsQuery.isLoading || assessmentsQuery.isLoading}
        error={
          skillsQuery.isError || assessmentsQuery.isError
            ? 'Impossibile caricare le skill della competenza.'
            : null
        }
        emptyMessage="Questa competenza non ha skill collegate."
        defaultSort={{ key: 'skillName', direction: 'asc' }}
        paginated={false}
      />
    </section>
  );
}
